import { useState, useEffect, useRef } from 'react';
import {
  Menu, X, LogOut, User as UserIcon,
  Sun, Wrench, Users, ClipboardList, Calendar, Tag, Building2,
  Camera, BarChart3, DollarSign, UserPlus, Gift,
  Settings as SettingsIcon, ChevronLeft, ChevronRight, MapPin,
  Package, Truck, FileText, Warehouse, Radio, ShoppingCart, MessageCircle, MessageSquare,
  RefreshCw
} from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import LandingPage from './LandingPage';
import Login from './Login';
import HomeDashboard from './HomeDashboard';
import CompanySignup from './CompanySignup';
import InstallPrompt from './InstallPrompt';
import { ModalProvider } from './AppModal';
import { ThemeProvider, useTheme } from './ThemeContext';
import { FeatureProvider, UpgradePrompt } from './FeatureContext';
import PublicQuoteView from './PublicQuoteView';
import './mobile-utils.css'; // iOS/Android mobile optimizations
import { captureLocation } from './locationTracker';

// Import all modules
import CustomersLeads from './CustomersLeads';
import Today from './Today';
import QuoteBuilder from './quote-builder';
import WorkOrders from './WorkOrders';
import ExpressJob from './ExpressJobWorkflow';
import ProductsPricing from './ProductsPricing';
import ReportingFinance from './ReportingFinance';
import PlanningScheduling from './planning-scheduling';
import FieldTechDailyView from './field-tech-daily-view';
import Settings from './Settings';
import TeamManagement from './TeamManagement';
import StripeCallback from './StripeCallback';
import SalesTool from './SalesTool';
import SupportChat from './SupportChat';
import SyncStatusIndicator from './SyncStatusIndicator';
import VendorManagement from './VendorManagement';
import Money from './Money';
import WelcomePage from './WelcomePage';
import ProductOnboarding from './ProductOnboarding';
import SmartAssistant from './SmartAssistant';
import { GuidedTourProvider, useGuidedTour } from './GuidedTourContext';
import GuidedTour from './GuidedTour';
import { PRODUCT_TOUR_STEPS } from './ProductTourSteps';
import { usePresence } from './usePresence';
import NotificationBell from './NotificationBell';
import FeedbackModal from './FeedbackModal';
import TrialBanner from './TrialBanner';
import CustomerSignupForm from './CustomerSignupForm';
import TermsPage from './TermsPage';
import PrivacyPage from './PrivacyPage';
import AuthCallback from './AuthCallback';
import LocalResources from './LocalResources';
import InventoryPage from './InventoryPage';
import JobTemplatesPage from './JobTemplatesPage';
import VehiclesPage from './VehiclesPage';
import LocationsPage from './LocationsPage';
import ReorderListPage from './ReorderListPage';
import InventoryReportsPage from './InventoryReportsPage';
import PurchaseOrdersPage from './PurchaseOrdersPage';
import TruckLoadoutPage from './TruckLoadoutPage';
import InventoryHealthWidget from './InventoryHealthWidget';
import DispatchBoard from './DispatchBoard';
import DemoTierSwitcher from './DemoTierSwitcher';

// Module-level guards — survive React re-renders, strict mode, remounts
let _loginProcessing = false;
let _authHandled = false;

function App() {
  const { theme, colors, updateTheme, isDark } = useTheme();
  const workvanLogoSrc = isDark ? '/WorkVan-logo-dark.png' : '/workvan-logo.png';
  const { startTour } = useGuidedTour();
  // Read initial module from URL hash (e.g. #customers), but not OAuth hashes
  const getInitialModule = () => {
    const hash = window.location.hash;
    if (hash && !hash.includes('access_token') && !hash.includes('refresh_token')) {
      return hash.substring(1) || 'home';
    }
    return 'home';
  };
  const [currentModule, setCurrentModule] = useState(getInitialModule);
  const [moduleData, setModuleData] = useState(null); // Data to pass to modules
  const [moduleKey, setModuleKey] = useState(0); // Force module remount on navigation
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [supportChatOpen, setSupportChatOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [hasCompany, setHasCompany] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [checkingCompany, setCheckingCompany] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false); // For landing page
  const [loginMode, setLoginMode] = useState('login'); // 'login' or 'signup'
  const [showWelcomePage, setShowWelcomePage] = useState(false); // For post-checkout welcome
  const [setupJustCompleted, setSetupJustCompleted] = useState(false); // Track if user just completed setup
  const [showProductOnboarding, setShowProductOnboarding] = useState(false); // For first product setup
  const [productOnboardingChecked, setProductOnboardingChecked] = useState(false); // Track if we've checked
  const [showSplash, setShowSplash] = useState(() => {
    if (sessionStorage.getItem('wv_splash_shown')) return false;
    // Only show for returning users (have auth token) or PWA standalone
    const hasSession = !!localStorage.getItem('sb-wrgmicftdykgaygbnkqn-auth-token');
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    return hasSession || isStandalone;
  });
  const [splashFading, setSplashFading] = useState(false);
  const splashVideoRef = useRef(null);
  const [jobsForAssistant, setJobsForAssistant] = useState([]); // Jobs for Smart Assistant
  const [pendingInviteId, setPendingInviteId] = useState(null); // Invite ID from URL
  const [inviteData, setInviteData] = useState(null); // Fetched invite details (company name, role, email)
  const [showPasswordReset, setShowPasswordReset] = useState(false); // Password reset after email link
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false); // Logout confirmation
  const hasCompanyRef = useRef(null); // Ref to track hasCompany for auth listener closure
  const loadingRef = useRef(true); // Ref to track loading state for timeout
  const isProcessingAuthRef = useRef(false); // Prevent re-entry during auth processing
  const hasHandledAuthRef = useRef(false); // One-shot: once auth is processed, never process again

  // Pull-to-refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pullStartY = useRef(0);
  const mainRef = useRef(null);

  // Version check state
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const initialBundleHash = useRef(null);

  // Animated splash screen — dismiss on video end or 4s fallback
  useEffect(() => {
    if (!showSplash) return;
    sessionStorage.setItem('wv_splash_shown', 'true');
    const fallback = setTimeout(() => {
      setSplashFading(true);
      setTimeout(() => setShowSplash(false), 600);
    }, 4000);
    return () => clearTimeout(fallback);
  }, [showSplash]);

  const handleSplashEnd = () => {
    setSplashFading(true);
    setTimeout(() => setShowSplash(false), 600);
  };

  // Pull-to-refresh gesture handler
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const THRESHOLD = 80;

    const onTouchStart = (e) => {
      if (el.scrollTop === 0 && !isRefreshing) {
        pullStartY.current = e.touches[0].clientY;
        setIsPulling(true);
      }
    };

    const onTouchMove = (e) => {
      if (!isPulling || isRefreshing) return;
      const y = e.touches[0].clientY;
      const dist = Math.max(0, y - pullStartY.current);
      if (dist > 0 && el.scrollTop === 0) {
        // Dampen the pull distance for a natural feel
        setPullDistance(Math.min(dist * 0.5, 120));
        if (dist > 10) {
          e.preventDefault(); // Prevent native scroll while pulling
        }
      }
    };

    const onTouchEnd = () => {
      if (!isPulling) return;
      if (pullDistance >= THRESHOLD) {
        setIsRefreshing(true);
        setPullDistance(THRESHOLD * 0.5);
        setTimeout(() => window.location.reload(), 300);
      } else {
        setPullDistance(0);
      }
      setIsPulling(false);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [isPulling, pullDistance, isRefreshing]);

  // Auto version detection - check every 5 minutes for new deployment
  useEffect(() => {
    // Fetch the main HTML page and extract the main JS bundle hash
    const extractBundleHash = async () => {
      try {
        const res = await fetch('/?_vc=' + Date.now(), {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        const html = await res.text();
        // Look for main.HASH.js or main.HASH.chunk.js pattern in the HTML
        const match = html.match(/\/static\/js\/main\.[a-f0-9]+\.js/);
        return match ? match[0] : null;
      } catch {
        return null;
      }
    };

    // Capture current bundle hash on load
    const initCheck = async () => {
      const hash = await extractBundleHash();
      if (hash) {
        initialBundleHash.current = hash;
      }
    };
    initCheck();

    // Check every 5 minutes
    const interval = setInterval(async () => {
      if (!initialBundleHash.current) return;
      const currentHash = await extractBundleHash();
      if (currentHash && currentHash !== initialBundleHash.current) {
        setNewVersionAvailable(true);
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // Track user presence for live chat feature
  usePresence(user, companyData?.id, currentModule);

  // Capture tech location on app open
  useEffect(() => {
    if (user?.id && companyData?.id) {
      captureLocation(user.id, companyData.id, 'app_open');
    }
  }, [user?.id, companyData?.id]);

  // Increment moduleKey on navigation to force component remount
  useEffect(() => {
    setModuleKey(k => k + 1);
  }, [currentModule]);

  // Navigate to a module and update the URL hash + browser history
  const navigateTo = (module, data = null, { replace = false } = {}) => {
    setCurrentModule(module);
    setModuleData(data);
    const newHash = `#${module}`;
    if (replace) {
      window.history.replaceState({ module }, '', newHash);
    } else if (window.location.hash !== newHash) {
      window.history.pushState({ module }, '', newHash);
    }
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (e) => {
      const hash = window.location.hash;
      if (hash && !hash.includes('access_token')) {
        const module = hash.substring(1) || 'home';
        setCurrentModule(module);
        setModuleData(null);
      } else if (!hash) {
        setCurrentModule('home');
        setModuleData(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Sync hash on initial load if it was read from hash
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash === '#') {
      window.history.replaceState({ module: 'home' }, '', window.location.pathname + window.location.search + '#home');
    }
  }, []);

  // Keep refs in sync with state (for use in closures)
  useEffect(() => {
    hasCompanyRef.current = hasCompany;
  }, [hasCompany]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  // Failsafe: if checkingCompany stays true for more than 15 seconds, force it off
  useEffect(() => {
    if (checkingCompany) {
      const timeout = setTimeout(() => {
        console.warn('[Auth] checkingCompany stuck for 15s, forcing off');
        setCheckingCompany(false);
      }, 15000);
      return () => clearTimeout(timeout);
    }
  }, [checkingCompany]);

  // When user is set (from any source), fetch profile + company data
  // Uses RAW FETCH to bypass Supabase JS client's internal session lock.
  // The Supabase client deadlocks when onAuthStateChange fires 10+ times rapidly,
  // blocking all supabaseData.from() calls. Raw fetch sidesteps this entirely.
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (!user?.id) {
      hasFetchedRef.current = false; // Reset on logout
      return;
    }
    if (hasFetchedRef.current) return; // Only fetch once per user
    hasFetchedRef.current = true;
    console.log('[Auth] useEffect: fetching profile + company via raw fetch for', user.email);

    let cancelled = false;
    const SUPABASE_REST = 'https://wrgmicftdykgaygbnkqn.supabase.co/rest/v1';
    const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6IndyZ21pY2Z0ZHlrZ2F5Z2Jua3FuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjU2MDIzNTYsImV4cCI6MjA4MTE3ODM1Nn0.CpRdUWBAti0qGCLKIZCaydk4h4Nnifx6apBEwy0k4E8';

    (async () => {
      try {
        // Get auth token from localStorage (set by Supabase auth on login)
        const tokenData = JSON.parse(localStorage.getItem('sb-wrgmicftdykgaygbnkqn-auth-token') || 'null');
        const token = tokenData?.access_token;
        if (!token) {
          console.error('[Auth] No auth token in localStorage, falling back to supabase client');
          await fetchUserProfile(user.id);
          await checkUserCompany(user.id);
          return;
        }
        if (cancelled) return;

        const headers = {
          'apikey': ANON_KEY,
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        };

        // 1. Fetch user profile
        console.log('[Auth] Raw fetch: fetching profile...');
        const profileRes = await fetch(
          `${SUPABASE_REST}/user_profiles?select=*&id=eq.${user.id}`,
          { headers }
        );
        if (cancelled) return;
        if (profileRes.ok) {
          const profiles = await profileRes.json();
          if (profiles && profiles.length > 0) {
            setUserProfile(profiles[0]);
            console.log('[Auth] Raw fetch: profile loaded');
          } else {
            // No profile — create one (self-healing)
            console.log('[Auth] Raw fetch: no profile found, creating...');
            const createRes = await fetch(
              `${SUPABASE_REST}/user_profiles`,
              {
                method: 'POST',
                headers: { ...headers, 'Prefer': 'return=representation' },
                body: JSON.stringify({ id: user.id, email: user.email, role: 'admin' })
              }
            );
            if (createRes.ok) {
              const created = await createRes.json();
              if (created && created.length > 0) setUserProfile(created[0]);
            }
          }
        } else {
          console.error('[Auth] Raw fetch profile failed:', profileRes.status, await profileRes.text());
        }

        if (cancelled) return;

        // 2. Check company (cache first)
        const hasPendingInvite = pendingInviteId || localStorage.getItem('pending_invite_id');
        const cachedCompany = localStorage.getItem(`company_${user.id}`);
        if (cachedCompany && !hasPendingInvite) {
          const cached = JSON.parse(cachedCompany);
          // Invalidate cache if missing fields added in newer versions
          if (cached && 'stripe_connect_id' in cached && 'square_merchant_id' in cached && 'trial_ends_at' in cached) {
            setHasCompany(true);
            setCompanyData(cached);
            console.log('[Auth] Raw fetch: company loaded from cache');
            return;
          } else {
            console.log('[Auth] Raw fetch: cache stale (missing payment processor fields), re-fetching');
            localStorage.removeItem(`company_${user.id}`);
          }
        }

        console.log('[Auth] Raw fetch: fetching user_companies...');
        const ucRes = await fetch(
          `${SUPABASE_REST}/user_companies?select=company_id,role&user_id=eq.${user.id}&limit=1`,
          { headers }
        );
        if (cancelled) return;
        const userCompanies = ucRes.ok ? await ucRes.json() : [];

        if (!userCompanies || userCompanies.length === 0) {
          console.log('[Auth] Raw fetch: no company found, falling back to checkUserCompany for invite handling');
          // Fall back to supabase client for invite handling — this is a first-time flow
          // where the session lock is less likely to be an issue
          await checkUserCompany(user.id);
          return;
        }

        const companyId = userCompanies[0].company_id;
        const userRole = userCompanies[0].role;
        const companyFields = 'id,name,tier,subscription_status,trial_ends_at,stripe_customer_id,stripe_subscription_id,stripe_connect_id,square_merchant_id,max_calendars,texting_enabled,logo_url,sales_tax_mode,menu_order,default_view,onboarding_products_skipped,onboarding_tour_completed,role_permissions,inventory_mode,inventory_enabled,is_demo';

        console.log('[Auth] Raw fetch: fetching company...');
        const companyRes = await fetch(
          `${SUPABASE_REST}/companies?select=${companyFields}&id=eq.${companyId}`,
          { headers }
        );
        if (cancelled) return;
        const companies = companyRes.ok ? await companyRes.json() : [];

        if (!companies || companies.length === 0) {
          console.log('[Auth] Raw fetch: company not found');
          setHasCompany(false);
        } else {
          const company = companies[0];
          console.log('[Auth] Raw fetch: company loaded:', company.name);
          setHasCompany(true);
          setCompanyData({ ...company, user_role: userRole });
          localStorage.setItem(`company_${user.id}`, JSON.stringify({ ...company, user_role: userRole }));

          // Handle pending invite for existing users
          const storedInvite = pendingInviteId || localStorage.getItem('pending_invite_id');
          if (storedInvite) {
            // Delegate invite handling to checkUserCompany (runs after initial load)
            checkUserCompany(user.id).catch(() => {});
          }

          // Set default view based on role
          const hashModule = window.location.hash?.substring(1);
          const hasExplicitRoute = hashModule && hashModule !== 'home';
          if (!hasExplicitRoute) {
            if (userRole === 'field_tech') {
              navigateTo('field-view', null, { replace: true });
            } else if (company.default_view && company.default_view !== 'home') {
              navigateTo(company.default_view, null, { replace: true });
            }
          }
        }

        console.log('[Auth] Raw fetch: complete');
      } catch (e) {
        console.error('[Auth] Raw fetch error:', e);
        // Last resort fallback to supabase client
        try {
          await fetchUserProfile(user.id);
          await checkUserCompany(user.id);
        } catch (e2) {
          console.error('[Auth] Fallback also failed:', e2);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setCheckingCompany(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check authentication status on mount
  useEffect(() => {
    // Capture OAuth code from URL FIRST (before any URL cleaning)
    const urlParams = new URLSearchParams(window.location.search);
    const oauthCode = urlParams.get('code');

    // Capture affiliate referral from URL immediately (before any navigation)
    const ref = urlParams.get('ref');
    if (ref) {
      const existingRef = localStorage.getItem('affiliate_ref');
      if (!existingRef) {
        localStorage.setItem('affiliate_ref', ref);
        localStorage.setItem('affiliate_ref_captured_at', new Date().toISOString());
        document.cookie = `affiliate_ref=${ref}; max-age=${60 * 24 * 60 * 60}; path=/; SameSite=Lax`;
      }
      // Clean URL without reload
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    // Capture test mode flag from URL (used by internal test accounts system)
    const testParam = urlParams.get('test');
    if (testParam === '1') {
      localStorage.setItem('test_mode', 'true');
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    // Capture team invite ID from URL
    const inviteId = urlParams.get('invite');
    if (inviteId) {
      setPendingInviteId(inviteId);
      localStorage.setItem('pending_invite_id', inviteId);
      // Fetch invite details for display (uses SECURITY DEFINER function, no auth needed)
      (async () => {
        try {
          const { data: preview } = await supabaseData.rpc('get_invite_preview', { invite_id: inviteId });
          if (preview && preview.length > 0) {
            setInviteData({
              id: inviteId,
              email: preview[0].email,
              phone: preview[0].phone,
              role: preview[0].role,
              companyName: preview[0].company_name || 'a team'
            });
          }
        } catch (err) {
          // no-op
        }
      })();
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else {
      // Check if invite was stored before a redirect (e.g., email confirmation)
      const storedInvite = localStorage.getItem('pending_invite_id');
      if (storedInvite) {
        setPendingInviteId(storedInvite);
        // Also try to fetch invite details for stored invite
        (async () => {
          try {
            const { data: preview } = await supabaseData.rpc('get_invite_preview', { invite_id: storedInvite });
            if (preview && preview.length > 0) {
              setInviteData({
                id: storedInvite,
                email: preview[0].email,
                phone: preview[0].phone,
                role: preview[0].role,
                companyName: preview[0].company_name || 'a team'
              });
            }
          } catch (err) {
            // no-op
          }
        })();
      }
    }

    // Handle Stripe checkout return
    const setupStatus = urlParams.get('setup');
    if (setupStatus === 'success') {
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Clear cached company data so we fetch fresh data with stripe_customer_id
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('company_')) {
          localStorage.removeItem(key);
        }
      });
      // Store in localStorage in case session was lost during redirect
      localStorage.setItem('setup_just_completed', 'true');
      localStorage.setItem('setup_completed_at', Date.now().toString());
      // Set flags to show welcome page and skip payment check
      setSetupJustCompleted(true);
      setShowWelcomePage(true);
    } else if (setupStatus === 'cancelled') {
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Mark that setup was cancelled - user should still have their trial company
      // Don't clear session or company data - they can continue into the app
      localStorage.setItem('setup_cancelled', 'true');
    }

    // Check if setup was just completed (persisted in localStorage)
    // Expire after 1 hour to prevent stale flags from causing welcome page on future logins
    if (localStorage.getItem('setup_just_completed') === 'true') {
      const completedAt = parseInt(localStorage.getItem('setup_completed_at') || '0');
      if (completedAt && Date.now() - completedAt > 3600000) {
        // Stale flag — more than 1 hour old, clear it
        localStorage.removeItem('setup_just_completed');
        localStorage.removeItem('setup_completed_at');
        localStorage.removeItem('welcome_page_dismissed');
      } else {
        setSetupJustCompleted(true);
      }
    }

    // Handle Stripe Connect return (comes back to /settings?tab=payments)
    if (window.location.pathname === '/settings' || urlParams.get('tab') === 'payments') {
      // Store intent to go to settings after auth completes
      localStorage.setItem('navigate_to', 'settings');
      // Clean URL
      window.history.replaceState({}, document.title, '/');
    }

    // Handle invoice payment success return
    const invoicePaid = urlParams.get('invoice_paid');
    if (invoicePaid) {
      // Store intent to show success and go to work orders
      localStorage.setItem('navigate_to', 'work-orders');
      localStorage.setItem('invoice_paid_success', invoicePaid);
      // Clean URL
      window.history.replaceState({}, document.title, '/');
    }

    // Failsafe timeout - if loading takes more than 10 seconds, force it to complete
    const loadingTimeout = setTimeout(() => {
      if (loadingRef.current) {
        setLoading(false);
      }
    }, 10000);

    // Determine if this is an OAuth/hash flow that needs special handling
    const hasOAuthFlow = oauthCode || (window.location.hash && window.location.hash.includes('access_token'));

    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      // INITIAL_SESSION: fires once on page load with stored session (if any)
      // This is the primary handler for page reloads
      if (event === 'INITIAL_SESSION') {
        if (session?.user) {
          console.log('[Auth] INITIAL_SESSION - restoring user:', session.user.email);
          if (!hasHandledAuthRef.current) {
            setUser(session.user);
            hasHandledAuthRef.current = true;
          }
          // The useEffect watching user?.id will handle profile + company fetch
          return;
        }
        setLoading(false);
        return;
      }

      // Detect password recovery flow BEFORE everything else
      if (event === 'PASSWORD_RECOVERY') {
        console.log('[Auth] Password recovery detected');
        setShowPasswordReset(true);
        if (session?.user) {
          setUser(session.user);
        }
        return;
      }

      // SIGNED_IN and TOKEN_REFRESHED are handled by:
      // - INITIAL_SESSION (page reloads with existing session)
      // - handleLoginSuccess (new logins, called directly by Login component)
      // - checkUser (OAuth flows)
      // So we only need to handle SIGNED_OUT here.
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        setHasCompany(null);
        setCompanyData(null);
        hasHandledAuthRef.current = false; // Allow re-processing on next login
        _loginProcessing = false; // Reset module-level guard
        _authHandled = false; // Allow auth processing on next login
        localStorage.clear();
      }
    });

    // Only run checkUser for OAuth code/hash flows (normal sessions handled by INITIAL_SESSION)
    if (hasOAuthFlow) {
      checkUser(oauthCode);
    }

    // Fallback: if INITIAL_SESSION doesn't fire within 2 seconds, manually restore session
    // Some Supabase client versions silently fail to emit INITIAL_SESSION on page reload
    const sessionFallback = setTimeout(async () => {
      if (!hasHandledAuthRef.current && loadingRef.current) {
        console.log('[Auth] INITIAL_SESSION never fired, checking localStorage token...');
        try {
          const tokenData = JSON.parse(localStorage.getItem('sb-wrgmicftdykgaygbnkqn-auth-token') || 'null');
          if (tokenData?.user && tokenData?.access_token) {
            const expiresAt = tokenData.expires_at;
            const now = Math.floor(Date.now() / 1000);
            if (!expiresAt || expiresAt > now) {
              console.log('[Auth] Session restored from localStorage:', tokenData.user.email);
              // DO NOT call supabase.auth.setSession() here — it triggers onAuthStateChange
              // which causes an auth loop (7x re-entry). Instead, just set user directly.
              // Data queries use supabaseData client which reads the token from localStorage,
              // so the main Supabase client doesn't need to be hydrated.
              if (!hasHandledAuthRef.current) {
                setUser(tokenData.user);
                hasHandledAuthRef.current = true;
              }
              return;
            }
          }
          console.log('[Auth] No valid session in localStorage');
          setLoading(false);
        } catch (e) {
          console.error('[Auth] Session fallback error:', e);
          setLoading(false);
        }
      }
    }, 2000);

    return () => {
      clearTimeout(loadingTimeout);
      clearTimeout(sessionFallback);
      authListener?.subscription.unsubscribe();
    };
  }, []); // No dependencies - only run on mount

  const checkUser = async (oauthCode) => {
    // Prevent re-entry - if we're already processing auth, skip
    if (hasHandledAuthRef.current) {
      return;
    }
    hasHandledAuthRef.current = true;

    try {
      let session = null;

      // Handle OAuth PKCE callback - explicitly exchange code for session
      if (oauthCode) {
        console.log('[Auth] OAuth code detected, exchanging for session...');
        const { data, error: codeError } = await supabase.auth.exchangeCodeForSession(oauthCode);
        // Clean URL immediately after exchange
        window.history.replaceState({}, '', window.location.pathname);
        if (codeError) {
          console.error('[Auth] Code exchange failed:', codeError.message, codeError);
          // Fall through to getSession in case auto-detection already handled it
        } else if (data?.session) {
          console.log('[Auth] Code exchange succeeded, user:', data.session.user.email);
          session = data.session;
        }
      }

      // Track if this is a password recovery flow (needs to be accessible later)
      let isPasswordRecovery = false;

      // Handle implicit OAuth flow (hash fragment with access_token)
      // detectSessionInUrl is disabled to prevent race conditions, so we handle it here
      if (!session && window.location.hash && window.location.hash.includes('access_token')) {
        console.log('[Auth] Hash fragment detected, extracting tokens...');
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const expiresAt = hashParams.get('expires_at');
        const tokenType = hashParams.get('type'); // Check for recovery type

        // IMMEDIATELY clear the hash to prevent re-processing on any re-render
        window.history.replaceState({}, '', window.location.pathname);
        console.log('[Auth] Hash cleared from URL');

        // Check if this is a password recovery flow
        isPasswordRecovery = tokenType === 'recovery';
        if (isPasswordRecovery) {
          console.log('[Auth] Password recovery flow detected from hash');
        }

        // Check if token is expired
        if (expiresAt && parseInt(expiresAt) < Math.floor(Date.now() / 1000)) {
          console.warn('[Auth] Token in URL has expired, clearing URL and trying refresh token...');
          window.history.replaceState({}, '', window.location.pathname);
          // Try to use the refresh token instead
          if (refreshToken) {
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession({ refresh_token: refreshToken });
            if (!refreshError && refreshData?.session) {
              console.log('[Auth] Session refreshed successfully, user:', refreshData.session.user.email);
              session = refreshData.session;
              // Show password reset screen if this was a recovery flow
              if (isPasswordRecovery) {
                setShowPasswordReset(true);
              }
            } else {
              console.error('[Auth] Refresh failed:', refreshError?.message || 'No session returned');
            }
          }
        } else if (accessToken && refreshToken) {
          const { data, error: hashError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          // URL already cleaned above
          if (hashError) {
            console.error('[Auth] setSession from hash failed:', hashError.message);
          } else if (data?.session) {
            console.log('[Auth] Session set from hash, user:', data.session.user.email);
            session = data.session;
            // Show password reset screen if this was a recovery flow
            if (isPasswordRecovery) {
              console.log('[Auth] Showing password reset screen for recovery flow');
              setShowPasswordReset(true);
            }
          }
        }
      }

      // Fallback: check for existing session (returning users, page refresh)
      if (!session) {
        const { data: { session: existingSession }, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('[Auth] getSession error:', sessionError.message);
        }
        session = existingSession;
      }

      if (session?.user) {
        console.log('[Auth] Setting user and checking company...');
        setUser(session.user);

        // Skip company check if this is a password recovery flow - they just need to reset password
        if (isPasswordRecovery) {
          console.log('[Auth] Password recovery mode - skipping company check');
        } else {
          await fetchUserProfile(session.user.id);
          await checkUserCompany(session.user.id);
          console.log('[Auth] Company check complete');
          warmUpTables();
        }
      } else {
        console.log('[Auth] No session, showing landing page');
      }
    } catch (error) {
      console.error('[Auth] Error in checkUser:', error);
    } finally {
      setLoading(false);
      console.log('[Auth] Loading set to false');
    }
  };

  const checkUserCompany = async (userId) => {
    console.log('[CompanyCheck] Starting check for user:', userId);
    try {

      // Check localStorage cache first (skip if pending invite needs processing)
      const hasPendingInvite = pendingInviteId || localStorage.getItem('pending_invite_id');
      const cachedCompany = localStorage.getItem(`company_${userId}`);
      if (cachedCompany && !hasPendingInvite) {
        const cached = JSON.parse(cachedCompany);
        if (cached && 'stripe_connect_id' in cached && 'square_merchant_id' in cached && 'trial_ends_at' in cached) {
          setHasCompany(true);
          setCompanyData(cached);
          setCheckingCompany(false);
          return;
        } else {
          localStorage.removeItem(`company_${userId}`);
        }
      }

      setCheckingCompany(true);

      const { data: userCompanies, error: linkError } = await supabase
        .from('user_companies')
        .select('company_id, role')
        .eq('user_id', userId)
        .limit(1);

      if (linkError) {
        setHasCompany(false);
        setCheckingCompany(false);
        return;
      }

      if (!userCompanies || userCompanies.length === 0) {
        // No company yet - try to accept a pending invitation
        const storedInviteId = pendingInviteId || localStorage.getItem('pending_invite_id');

        // Priority 1: Accept by stored invite ID (from URL link) using RPC
        if (storedInviteId) {
          try {
            const { data: result, error: rpcError } = await supabaseData.rpc('accept_team_invitation', { invite_id: storedInviteId });

            if (!rpcError && result?.success) {
              // Clear stored invite ID
              localStorage.removeItem('pending_invite_id');
              setPendingInviteId(null);
              setInviteData(null);
              // Re-run checkUserCompany to load the newly linked company
              await checkUserCompany(userId);
              return;
            }
          } catch (err) {
            // no-op
          }
        }

        // Priority 2: Fall back to email match (for invites without URL click)
        try {
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          const userEmail = currentUser?.email?.toLowerCase();

          if (userEmail) {
            const { data: inviteByEmail } = await supabase
              .from('team_invitations')
              .select('id')
              .eq('email', userEmail)
              .eq('status', 'pending')
              .limit(1)
              .maybeSingle();

            if (inviteByEmail) {
              const { data: result } = await supabaseData.rpc('accept_team_invitation', { invite_id: inviteByEmail.id });
              if (result?.success) {
                localStorage.removeItem('pending_invite_id');
                setPendingInviteId(null);
                setInviteData(null);
                await checkUserCompany(userId);
                return;
              }
            }
          }
        } catch (inviteError) {
          // no-op
        }

        console.log('[CompanyCheck] No company found for user, setting hasCompany=false');
        setHasCompany(false);
        setCheckingCompany(false);
        console.log('[CompanyCheck] States set: hasCompany=false, checkingCompany=false');
        return;
      }

      // If user already has a company but arrived via invite link, accept the invite too
      // (This handles the case where an existing user is invited to join another team)
      const storedInviteForExisting = pendingInviteId || localStorage.getItem('pending_invite_id');
      if (storedInviteForExisting) {
        try {
          const { data: acceptResult, error: rpcError } = await supabaseData.rpc('accept_team_invitation', { invite_id: storedInviteForExisting });

          // Helper to switch to invited company
          const switchToCompany = async (targetCompanyId, targetRole) => {
            localStorage.removeItem(`company_${userId}`);
            const { data: newCompany } = await supabase
              .from('companies')
              .select('id, name, tier, subscription_status, trial_ends_at, stripe_customer_id, stripe_subscription_id, stripe_connect_id, square_merchant_id, max_calendars, texting_enabled, logo_url, sales_tax_mode, menu_order, default_view, onboarding_products_skipped, onboarding_tour_completed, role_permissions, inventory_mode, is_demo')
              .eq('id', targetCompanyId)
              .single();
            if (newCompany) {
              setHasCompany(true);
              setCompanyData({ ...newCompany, user_role: targetRole });
              localStorage.setItem(`company_${userId}`, JSON.stringify({ ...newCompany, user_role: targetRole }));
              setCheckingCompany(false);
              setProductOnboardingChecked(true);
              setShowProductOnboarding(false);
              if (targetRole === 'field_tech') {
                navigateTo('field-view', null, { replace: true });
              }
              return true;
            }
            return false;
          };

          let handled = false;

          if (!rpcError && acceptResult?.success) {
            if (acceptResult.company_id) {
              handled = await switchToCompany(acceptResult.company_id, acceptResult.role || 'member');
            }
          } else {
            // RPC failed or invite already accepted - look up the invite to get the company_id
            // then check if user already has a link to it and switch
            try {
              const { data: inviteRecord } = await supabaseData.rpc('get_invite_preview', { invite_id: storedInviteForExisting });
              if (inviteRecord && inviteRecord.length > 0) {
                const inviteCompanyId = inviteRecord[0].company_id;
                const inviteRole = inviteRecord[0].role;

                // Check if user already has a link to this company
                const { data: existingLink } = await supabase
                  .from('user_companies')
                  .select('company_id, role')
                  .eq('user_id', userId)
                  .eq('company_id', inviteCompanyId)
                  .maybeSingle();

                if (existingLink) {
                  handled = await switchToCompany(existingLink.company_id, existingLink.role);
                } else {
                  // No link yet - create one directly
                  const { error: insertErr } = await supabase
                    .from('user_companies')
                    .insert([{ user_id: userId, company_id: inviteCompanyId, role: inviteRole }]);
                  if (!insertErr) {
                    handled = await switchToCompany(inviteCompanyId, inviteRole);
                  }
                }
              }
            } catch (lookupErr) {
              // no-op
            }
          }

          // Clear invite tracking
          localStorage.removeItem('pending_invite_id');
          setPendingInviteId(null);
          setInviteData(null);

          if (handled) return;
        } catch (err) {
          localStorage.removeItem('pending_invite_id');
          setPendingInviteId(null);
          setInviteData(null);
        }
      }

      const companyId = userCompanies[0].company_id;
      const userRole = userCompanies[0].role;

      const { data: company, error: companyError } = await supabase
        .from('companies')
        .select('id, name, tier, subscription_status, trial_ends_at, stripe_customer_id, stripe_subscription_id, stripe_connect_id, square_merchant_id, max_calendars, texting_enabled, logo_url, sales_tax_mode, menu_order, default_view, onboarding_products_skipped, onboarding_tour_completed, role_permissions, inventory_mode, is_demo')
        .eq('id', companyId)
        .single();

      if (companyError || !company) {
        console.log('[CompanyCheck] Company query error or no company:', companyError?.message);
        setHasCompany(false);
        setCheckingCompany(false);
        return;
      }

      // Let trialing users in even if Stripe checkout wasn't completed yet.
      // The webhook may be slow, or the user may have abandoned checkout.
      // They still have their trial period — prompt for payment from within the app.

      console.log('[CompanyCheck] Company found:', company.name, 'Tier:', company.tier);
      setHasCompany(true);
      setCompanyData({ ...company, user_role: userRole });
      localStorage.setItem(`company_${userId}`, JSON.stringify({ ...company, user_role: userRole }));
      setCheckingCompany(false);

      // Set default view based on role, then company config
      // Only override if no hash route was already set by the user
      const hashModule = window.location.hash?.substring(1);
      const hasExplicitRoute = hashModule && hashModule !== 'home';
      if (!hasExplicitRoute) {
        if (userRole === 'field_tech') {
          navigateTo('field-view', null, { replace: true });
        } else if (company.default_view && company.default_view !== 'home') {
          navigateTo(company.default_view, null, { replace: true });
        }
      }
    } catch (error) {
      console.error('[CompanyCheck] Unexpected error:', error);
      setHasCompany(false);
    } finally {
      setCheckingCompany(false);
    }
  };

  const warmUpTables = () => {
    // Fire off queries to all tables in parallel to "wake them up"
    // This prevents the first real query from timing out
    // Fire-and-forget — not awaited
    const tables = [
      'customers',
      'leads',
      'quotes',
      'jobs',
      'skus',
      'pricing_tables',
      'commission_partners',
      'invoices',
      'companies',
      'user_companies'
    ];

    Promise.allSettled(
      tables.map(table => supabaseData.from(table).select('id').limit(1))
    );
  };

  const fetchUserProfile = async (userId) => {
    try {
      // Try to fetch existing profile
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 = no rows returned, which is fine - we'll create one
      }

      if (profile) {
        setUserProfile(profile);
      } else {
        // Self-healing: create profile if missing
        const { data: authUser } = await supabase.auth.getUser();
        if (authUser?.user) {
          const { data: newProfile, error: insertError } = await supabase
            .from('user_profiles')
            .upsert({
              id: userId,
              email: authUser.user.email,
              role: 'admin'
            }, { onConflict: 'id' })
            .select()
            .single();

          if (insertError) {
            // no-op
          } else if (newProfile) {
            setUserProfile(newProfile);
          }
        }
      }
    } catch (err) {
      // no-op
    }
  };

  // Handle profile updates from Settings component
  const handleProfileUpdate = (updatedProfile) => {
    if (updatedProfile) {
      setUserProfile(prev => ({ ...prev, ...updatedProfile }));
    }
    // Also refresh from server to ensure consistency
    if (user?.id) {
      fetchUserProfile(user.id);
    }
  };

  // Explicit login success handler - called directly by Login component
  // Uses isProcessingAuthRef to block onAuthStateChange from duplicate processing
  const handleLoginSuccess = async (session) => {
    if (!session?.user || _loginProcessing) return;
    _loginProcessing = true;
    // Just set the user — a useEffect will handle the rest
    console.log('[Auth] Login success, setting user:', session.user.email);
    setUser(session.user);
    setShowLoginForm(false);
    hasHandledAuthRef.current = true;
    // Navigate to home (or stored destination) after login
    const savedNav = localStorage.getItem('navigate_to');
    if (savedNav) {
      localStorage.removeItem('navigate_to');
      navigateTo(savedNav, null, { replace: true });
    } else {
      navigateTo('home', null, { replace: true });
    }
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
    
    // Close mobile sidebar if open
    setSidebarOpen(false);
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  // Handle escape key to close logout modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showLogoutConfirm) {
        cancelLogout();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showLogoutConfirm]);

  const confirmLogout = async () => {
    // Close modal immediately
    setShowLogoutConfirm(false);

    // Clear any cached data and setup flags
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('company_') || key.startsWith('supabase.') || key.includes('auth'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    localStorage.removeItem('setup_just_completed');
    localStorage.removeItem('welcome_page_dismissed');
    localStorage.removeItem('setup_cancelled');

    // Clear state immediately
    setUser(null);
    setUserProfile(null);
    setHasCompany(null);
    setCompanyData(null);
    navigateTo('home', null, { replace: true });

    // Sign out from Supabase (don't await - let it happen in background)
    supabase.auth.signOut().catch(err => {
      console.error('Logout error:', err);
    });

    // Force reload to clear any in-memory state
    window.location.replace('/');
  };

  const handlePasswordReset = async () => {
    // Validate password
    if (!newPassword || newPassword.length < 6) {
      setPasswordResetError('Password must be at least 6 characters');
      return;
    }
    
    // Check for leading/trailing spaces
    if (newPassword !== newPassword.trim()) {
      setPasswordResetError('Password cannot contain leading or trailing spaces');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordResetError('Passwords do not match');
      return;
    }
    
    setPasswordResetLoading(true);
    setPasswordResetError(null);
    
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      
      // Clear password reset state
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmPassword('');
      
      // Show success message and redirect to login
      alert('Password updated successfully! Please sign in with your new password.');
      
      // Sign out to force re-login with new password
      await supabase.auth.signOut();
      setUser(null);
      setUserProfile(null);
      setHasCompany(null);
      setCompanyData(null);
      localStorage.removeItem('setup_just_completed');
      localStorage.removeItem('welcome_page_dismissed');
      window.location.href = '/';
    } catch (err) {
      setPasswordResetError(err.message);
    } finally {
      setPasswordResetLoading(false);
    }
  };

  // Handler for when company settings are updated from Settings component
  const handleCompanyUpdate = (updates) => {
    if (updates && companyData) {
      const updatedCompanyData = { ...companyData, ...updates };
      setCompanyData(updatedCompanyData);
      // Also update localStorage cache
      if (user?.id) {
        localStorage.setItem(`company_${user.id}`, JSON.stringify(updatedCompanyData));
      }
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clean up setup flags once user is fully in the app with confirmed payment
  // This prevents stale flags from causing issues (e.g., after Stripe Connect back-out)
  useEffect(() => {
    if (user && hasCompany && companyData?.stripe_customer_id) {
      // User is authenticated, has company with payment setup confirmed by webhook
      // Safe to clear all setup-related flags
      const hasSetupFlag = localStorage.getItem('setup_just_completed') === 'true';
      const hasWelcomeFlag = localStorage.getItem('welcome_page_dismissed') === 'true';

      if (hasSetupFlag || hasWelcomeFlag) {
        localStorage.removeItem('setup_just_completed');
        localStorage.removeItem('welcome_page_dismissed');
        setSetupJustCompleted(false);
      }
    }
  }, [user, hasCompany, companyData?.stripe_customer_id]);

  // Fetch jobs for Smart Assistant
  useEffect(() => {
    const fetchJobsForAssistant = async () => {
      if (!companyData?.id) return;

      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('jobs')
        .select('*, customers(name, phone, address)')
        .eq('company_id', companyData.id)
        .eq('scheduled_date', today)
        .in('status', ['scheduled', 'in-progress'])
        .order('scheduled_time', { ascending: true });

      setJobsForAssistant(data || []);
    };

    fetchJobsForAssistant();
    // Refresh every 5 minutes
    const interval = setInterval(fetchJobsForAssistant, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [companyData?.id]);

  // Handle stored navigation intent (e.g., returning from Stripe Connect)
  useEffect(() => {
    if (user && hasCompany && companyData) {
      const storedNav = localStorage.getItem('navigate_to');
      if (storedNav) {
        localStorage.removeItem('navigate_to');
        navigateTo(storedNav, null, { replace: true });
      }

      // Check for invoice payment success
      const invoicePaidSuccess = localStorage.getItem('invoice_paid_success');
      if (invoicePaidSuccess) {
        localStorage.removeItem('invoice_paid_success');
        // Show success notification
        alert('Payment received successfully! The invoice has been marked as paid.');
        // Store that we need to go to work-orders after reload
        localStorage.setItem('navigate_to', 'work-orders');
        // Force reload to get fresh invoice data
        window.location.reload();
      }
    }
  }, [user, hasCompany, companyData]);

  // Check if we should show product onboarding (for new owners/admins with 0 products)
  useEffect(() => {
    const checkProductOnboarding = async () => {
      // Skip for invited users (they shouldn't set up products)
      if (localStorage.getItem('pending_invite_id')) {
        setProductOnboardingChecked(true);
        return;
      }

      // Only check for owners/admins
      const userRole = companyData?.user_role;
      if (!companyData?.id || !['owner', 'admin'].includes(userRole)) {
        setProductOnboardingChecked(true);
        return;
      }

      // Check if already skipped or tour completed
      if (companyData?.onboarding_products_skipped || companyData?.onboarding_tour_completed) {
        setProductOnboardingChecked(true);
        return;
      }

      try {
        // Check if company has any products
        const { count, error } = await supabase
          .from('skus')
          .select('id', { count: 'exact', head: true })
          .eq('company_id', companyData.id);

        if (error) throw error;

        // Start guided tour if 0 products
        if (count === 0) {
          startTour(PRODUCT_TOUR_STEPS);
        }
      } catch (err) {
        // no-op
      } finally {
        setProductOnboardingChecked(true);
      }
    };

    if (user && hasCompany && companyData && !productOnboardingChecked) {
      checkProductOnboarding();
    }
  }, [user, hasCompany, companyData, productOnboardingChecked]);

  // Role-based menu access - use custom permissions if set, otherwise defaults
  const defaultRoleAccess = {
    owner: ['home', 'dispatch', 'customers', 'work-orders', 'schedule', 'products-pricing', 'vendors', 'inventory', 'reorder-list', 'inventory-reports', 'purchase-orders', 'truck-loadout', 'job-templates', 'vehicles', 'locations', 'local-resources', 'financial', 'money', 'team', 'referral', 'settings'],
    admin: ['home', 'dispatch', 'customers', 'work-orders', 'schedule', 'products-pricing', 'vendors', 'inventory', 'reorder-list', 'inventory-reports', 'purchase-orders', 'truck-loadout', 'job-templates', 'vehicles', 'locations', 'local-resources', 'sales-tool', 'financial', 'money', 'team', 'referral', 'settings'],
    office: ['home', 'dispatch', 'customers', 'work-orders', 'schedule', 'products-pricing', 'vendors', 'inventory', 'reorder-list', 'inventory-reports', 'purchase-orders', 'truck-loadout', 'job-templates', 'vehicles', 'locations', 'local-resources', 'sales-tool'],
    field_tech: ['field-view', 'work-orders', 'inventory', 'reorder-list', 'truck-loadout', 'locations', 'local-resources', 'sales-tool'],
    member: ['home', 'customers', 'work-orders', 'schedule', 'local-resources', 'sales-tool']
  };
  const customPerms = companyData?.role_permissions;
  const roleMenuAccess = {
    owner: defaultRoleAccess.owner, // Owner always has full access
    admin: customPerms?.admin || defaultRoleAccess.admin,
    office: customPerms?.office || defaultRoleAccess.office,
    field_tech: customPerms?.field_tech || defaultRoleAccess.field_tech,
    member: customPerms?.member || defaultRoleAccess.member
  };

  const defaultMenuItems = [
    { id: 'home', label: 'Today', icon: <Sun size={18} /> },
    { id: 'dispatch', label: 'Dispatch', icon: <Radio size={18} /> },
    { id: 'field-view', label: 'My Jobs', icon: <Wrench size={18} /> },
    { id: 'customers', label: 'Customers & Leads', icon: <Users size={18} /> },
    { id: 'work-orders', label: 'Work Orders', icon: <ClipboardList size={18} /> },
    { id: 'schedule', label: 'Schedule', icon: <Calendar size={18} /> },
    { id: 'products-pricing', label: 'Products & Pricing', icon: <Tag size={18} /> },
    { id: 'vendors', label: 'Vendors', icon: <Building2 size={18} /> },
    { id: 'inventory', label: 'Inventory', icon: <Package size={18} /> },
    { id: 'reorder-list', label: 'Reorder List', icon: <ShoppingCart size={18} /> },
    { id: 'inventory-reports', label: 'Inventory Reports', icon: <BarChart3 size={18} /> },
    { id: 'purchase-orders', label: 'Purchase Orders', icon: <ClipboardList size={18} /> },
    { id: 'truck-loadout', label: 'Truck Loadout', icon: <Truck size={18} /> },
    { id: 'job-templates', label: 'Job Templates', icon: <FileText size={18} /> },
    { id: 'vehicles', label: 'Vehicles', icon: <Truck size={18} /> },
    { id: 'locations', label: 'Locations', icon: <Warehouse size={18} /> },
    { id: 'local-resources', label: 'Local Resources', icon: <MapPin size={18} /> },
    { id: 'sales-tool', label: 'Sales Tool', icon: <Camera size={18} /> },
    { id: 'financial', label: 'Reporting & Finance', icon: <BarChart3 size={18} /> },
    { id: 'money', label: 'Money', icon: <DollarSign size={18} /> },
    { id: 'team', label: 'Team', icon: <UserPlus size={18} /> },
    { id: 'referral', label: 'Earn With WorkVan', icon: <Gift size={18} /> },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon size={18} /> }
  ];

  // Get menu items filtered by role, tier features, and custom order
  const getMenuItems = () => {
    const userRole = companyData?.user_role || 'member';
    const allowedItems = roleMenuAccess[userRole] || roleMenuAccess.member;
    const tier = companyData?.tier || 'pro';

    // Filter by role access
    let items = defaultMenuItems.filter(item => allowedItems.includes(item.id));

    // Filter by tier features
    // Map modules to required features (from FeatureContext TIER_FEATURES)
    const tierFeatureGates = {
      'team': 'teamManagement',
      'financial': 'advancedReporting'
    };
    const tierFeatures = {
      pro: { teamManagement: false, advancedReporting: false },
      team: { teamManagement: true, advancedReporting: true },
      growth: { teamManagement: true, advancedReporting: true },
      business: { teamManagement: true, advancedReporting: true },
      enterprise: { teamManagement: true, advancedReporting: true },
      unlimited: { teamManagement: true, advancedReporting: true }
    };
    const currentTierFeatures = tierFeatures[tier] || tierFeatures.pro;
    items = items.filter(item => {
      const requiredFeature = tierFeatureGates[item.id];
      if (!requiredFeature) return true;
      return currentTierFeatures[requiredFeature];
    });

    // Money module is Pro-only (Team+ uses Financial Dashboard instead)
    if (tier !== 'pro') {
      items = items.filter(item => item.id !== 'money');
    }

    // Hide inventory sub-pages when inventory is not enabled
    if (!companyData?.inventory_enabled) {
      items = items.filter(item => !['reorder-list', 'inventory-reports', 'purchase-orders', 'truck-loadout', 'job-templates', 'vehicles', 'locations'].includes(item.id));
    }

    // Locations only visible when multi_location mode is enabled
    if (companyData?.inventory_mode !== 'multi_location') {
      items = items.filter(item => item.id !== 'locations');
    }

    // Apply custom order if set — only show items in the saved order
    // Items removed from menu_order are hidden (user explicitly removed them)
    if (companyData?.menu_order && Array.isArray(companyData.menu_order)) {
      const orderedItems = companyData.menu_order
        .map(id => items.find(item => item.id === id))
        .filter(Boolean);

      items = orderedItems;
    }

    return items;
  };

  const menuItems = getMenuItems();

  // Navigation handler that supports passing data to modules
  const handleNavigate = (module, data = null) => {
    navigateTo(module, data);
  };

  const renderModule = () => {
    // Route-level permission enforcement
    // Some modules are accessed via actions (not menu) and should always be allowed
    const alwaysAllowed = ['express-job', 'quote-builder', 'work-orders-new-quote', 'work-orders-new-job'];
    if (companyData?.user_role && !alwaysAllowed.includes(currentModule)) {
      const userRole = companyData.user_role;
      const allowedModules = roleMenuAccess[userRole] || roleMenuAccess.member;
      if (!allowedModules.includes(currentModule)) {
        const defaultView = userRole === 'field_tech' ? 'field-view' : 'home';
        setTimeout(() => navigateTo(defaultView, null, { replace: true }), 0);
        return null;
      }
    }

    // Money is Pro-only — redirect Team+ to Financial Dashboard
    if (currentModule === 'money') {
      const tier = companyData?.tier || 'pro';
      if (tier !== 'pro') {
        setTimeout(() => navigateTo('financial', null, { replace: true }), 0);
        return null;
      }
    }

    // Tier gating — show upgrade prompt for modules the current tier can't access
    const moduleTierGates = {
      'team': { feature: 'teamManagement', requiredTier: 'team' },
      'financial': { feature: 'advancedReporting', requiredTier: 'team' },
      'dispatch': { feature: 'teamManagement', requiredTier: 'team' }
    };
    const tierGate = moduleTierGates[currentModule];
    if (tierGate) {
      const tier = companyData?.tier || 'pro';
      const gatedTiers = { pro: true }; // Only pro is gated for now
      if (gatedTiers[tier]) {
        return (
          <div style={{ padding: '2rem', maxWidth: '600px', margin: '2rem auto' }}>
            <UpgradePrompt
              feature={tierGate.feature}
              requiredTier={tierGate.requiredTier}
              currentTier={tier}
              onUpgrade={() => navigateTo('settings', { section: 'billing' })}
              onBack={() => navigateTo('home')}
            />
          </div>
        );
      }
    }

    switch(currentModule) {
      case 'home':
        return <Today companyId={companyData?.id} companyData={companyData} onNavigate={handleNavigate} user={user} isMobile={isMobile} />;
      case 'dispatch':
        return <DispatchBoard companyId={companyData?.id} onNavigate={handleNavigate} />;
      case 'customers':
        return <CustomersLeads companyId={companyData?.id} onNavigate={handleNavigate} isMobile={isMobile} textingEnabled={companyData?.texting_enabled} />;
      case 'work-orders':
      case 'quotes':
      case 'jobs':
      case 'invoicing':
        return <WorkOrders
          companyId={companyData?.id}
          onNavigate={handleNavigate}
          initialTab={currentModule === 'invoicing' ? 'invoices' : undefined}
          initialQuoteId={moduleData?.quoteId}
          initialJobId={moduleData?.jobId}
          initialInvoiceId={moduleData?.invoiceId}
          isMobile={isMobile}
          textingEnabled={companyData?.texting_enabled}
          userRole={companyData?.user_role}
        />;
      case 'quote-builder':
      case 'work-orders-new-quote':
      case 'work-orders-new-job':
        return <QuoteBuilder
          companyId={companyData?.id}
          onNavigate={handleNavigate}
          prefilledCustomer={moduleData?.prefilledCustomer}
          customerId={moduleData?.customerId}
          jobId={moduleData?.jobId}
          userRole={companyData?.user_role}
        />;
      case 'express-job':
        return <ExpressJob
          companyId={companyData?.id}
          onComplete={() => navigateTo('work-orders')}
          onClose={() => navigateTo('home')}
          userRole={companyData?.user_role}
          currentUserId={user?.id}
          prefilledCustomer={moduleData?.prefilledCustomer}
          initialWorkflowType={moduleData?.workflowType || null}
          isMobile={isMobile}
          textingEnabled={companyData?.texting_enabled}
        />;
      case 'products-pricing':
        return <ProductsPricing companyId={companyData?.id} />;
      case 'vendors':
        return <VendorManagement companyId={companyData?.id} />;
      case 'inventory':
        return <InventoryPage companyId={companyData?.id} onNavigate={handleNavigate} userId={user?.id} userRole={companyData?.user_role} />;
      case 'reorder-list':
        return <ReorderListPage companyId={companyData?.id} />;
      case 'inventory-reports':
        return <InventoryReportsPage companyId={companyData?.id} />;
      case 'purchase-orders':
        return <PurchaseOrdersPage companyId={companyData?.id} />;
      case 'truck-loadout':
        return <TruckLoadoutPage companyId={companyData?.id} />;
      case 'job-templates':
        return <JobTemplatesPage companyId={companyData?.id} />;
      case 'vehicles':
        return <VehiclesPage companyId={companyData?.id} />;
      case 'locations':
        return <LocationsPage companyId={companyData?.id} />;
      case 'local-resources':
        return <LocalResources companyId={companyData?.id} onNavigate={handleNavigate} />;
      case 'sales-tool':
        return <SalesTool companyId={companyData?.id} />;
      case 'money':
        return <Money companyId={companyData?.id} />;
      case 'financial':
        return <ReportingFinance companyId={companyData?.id} />;
      case 'schedule':
      case 'planning':
        return <PlanningScheduling
          companyId={companyData?.id}
          onNavigate={handleNavigate}
          createJobFromQuote={moduleData?.createJobFromQuote}
          prefillCustomer={moduleData?.customer}
          currentUserId={user?.id}
          isMobile={isMobile}
        />;
      case 'field-view':
        return <FieldTechDailyView />;
      case 'team':
        return <TeamManagement companyId={companyData?.id} userRole={companyData?.user_role} />;
      case 'referral':
        return <Settings
          user={user}
          userProfile={userProfile}
          onProfileUpdate={fetchUserProfile}
          companyId={companyData?.id}
          userRole={companyData?.user_role}
          onNavigate={handleNavigate}
          onCompanyUpdate={handleCompanyUpdate}
          initialSection="referral"
          isMobile={isMobile}
        />;
      case 'settings':
        return <Settings
          user={user}
          userProfile={userProfile}
          onProfileUpdate={handleProfileUpdate}
          companyId={companyData?.id}
          userRole={companyData?.user_role}
          onNavigate={handleNavigate}
          onCompanyUpdate={handleCompanyUpdate}
          initialSection={moduleData?.section}
          isMobile={isMobile}
        />;
      default:
        return <HomeDashboard onNavigate={(mod) => navigateTo(mod)} companyId={companyData?.id} />;
    }
  };

  // Public routes - must be checked BEFORE the loading gate
  // so they work without auth resolution
  if (window.location.pathname.startsWith('/signup/')) {
    const signupToken = window.location.pathname.replace('/signup/', '');
    return <CustomerSignupForm token={signupToken} />;
  }

  if (window.location.pathname.startsWith('/quote/')) {
    const quoteId = window.location.pathname.replace('/quote/', '');
    return <PublicQuoteView quoteId={quoteId} />;
  }

  if (window.location.pathname === '/terms') {
    return <TermsPage />;
  }

  if (window.location.pathname === '/privacy') {
    return <PrivacyPage />;
  }

  // Supabase auth redirect target — handles ?code= (PKCE) and #access_token=
  // (implicit) from signup confirmation, magic links, and recovery emails.
  // Must run BEFORE the auth-loading gate so we can actually establish a
  // session on this very page load.
  if (window.location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }

  // Password reset screen (shown after clicking email reset link)
  if (showPasswordReset) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0a0a0a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          maxWidth: '400px',
          width: '100%',
          padding: '2rem',
          border: '1px solid #3a3a3a'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <img src="/WorkVan-logo-dark.png" alt="Work Van" style={{ height: '100px', marginBottom: '1.5rem' }} />
            <h2 style={{ color: '#e0e0e0', margin: 0 }}>Set New Password</h2>
            <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '0.5rem' }}>Enter your new password below</p>
          </div>
          {passwordResetError && (
            <div style={{
              background: '#2a1a1a',
              border: '1px solid #e74c3c',
              borderRadius: '8px',
              padding: '0.75rem',
              marginBottom: '1rem',
              color: '#e74c3c',
              fontSize: '0.9rem'
            }}>
              {passwordResetError}
            </div>
          )}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontSize: '0.9rem' }}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="At least 6 characters"
              minLength={6}
              style={{
                width: '100%',
                padding: '0.875rem',
                background: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '8px',
                color: '#e0e0e0',
                fontSize: '16px'
              }}
            />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#e0e0e0', fontSize: '0.9rem' }}>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              style={{
                width: '100%',
                padding: '0.875rem',
                background: '#2a2a2a',
                border: '1px solid #3a3a3a',
                borderRadius: '8px',
                color: '#e0e0e0',
                fontSize: '16px'
              }}
            />
          </div>
          <button
            onClick={handlePasswordReset}
            disabled={passwordResetLoading}
            style={{
              width: '100%',
              padding: '0.875rem',
              background: '#F05A28',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: passwordResetLoading ? 'not-allowed' : 'pointer',
              opacity: passwordResetLoading ? 0.7 : 1
            }}
          >
            {passwordResetLoading ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#0a0a0a'
      }}>
        <div style={{
          fontSize: '1.5rem',
          color: '#e0e0e0',
          textAlign: 'center'
        }}>
          <img src="/WorkVan-logo-dark.png" alt="Work Van" style={{ width: '120px', marginBottom: '1.5rem' }} />
          <div>Loading Work Van...</div>
        </div>
      </div>
    );
  }

  // Animated splash screen (logged-in users and PWA only)
  if (showSplash) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        opacity: splashFading ? 0 : 1,
        transition: 'opacity 0.6s ease-out'
      }}>
        <video
          ref={splashVideoRef}
          src="/logo-animation.mp4"
          autoPlay
          muted
          playsInline
          onEnded={handleSplashEnd}
          style={{ width: '100vw', height: '100vh', objectFit: 'contain' }}
        />
      </div>
    );
  }

  // Handle Stripe callback route
  if (window.location.pathname === '/stripe/callback') {
    return (
      <StripeCallback
        onComplete={() => {
          // Clear the URL and navigate to settings
          navigateTo('settings', null, { replace: true });
        }}
      />
    );
  }

  if (!user) {
    // If user arrived via invite link AND we're done loading (not just waiting for session),
    // show login form directly with invite context
    // Check both React state AND localStorage (belt-and-suspenders for race conditions)
    const storedInviteCheck = pendingInviteId || inviteData || localStorage.getItem('pending_invite_id');
    if (storedInviteCheck && !loading) {
      return <Login
        onBack={() => {
          setPendingInviteId(null);
          setInviteData(null);
          localStorage.removeItem('pending_invite_id');
          setShowLoginForm(false);
        }}
        onLoginSuccess={handleLoginSuccess}
        initialMode="login"
        inviteData={inviteData}
        hasInvite={true}
      />;
    }
    // If setup was just completed but user isn't logged in, show login directly
    // This happens when session is lost during Stripe redirect
    if (setupJustCompleted || localStorage.getItem('setup_just_completed') === 'true') {
      return <Login onBack={() => {
        localStorage.removeItem('setup_just_completed');
        setSetupJustCompleted(false);
        setShowLoginForm(false);
      }} onLoginSuccess={handleLoginSuccess} initialMode="login" />;
    }
    // Show login form if user clicked login/signup from landing page
    if (showLoginForm) {
      return <Login onBack={() => setShowLoginForm(false)} onLoginSuccess={handleLoginSuccess} initialMode={loginMode} />;
    }
    // Otherwise show the marketing landing page
    return (
      <LandingPage
        onLogin={() => { setLoginMode('login'); setShowLoginForm(true); }}
        onSignup={() => { setLoginMode('signup'); setShowLoginForm(true); }}
      />
    );
  }

  // Wait for company check to complete (prevents flash of CompanySignup)
  // Also wait if hasCompany is true but companyData hasn't loaded yet
  if (hasCompany === null || checkingCompany || (hasCompany === true && companyData === null)) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#0a0a0a',
        padding: '2rem'
      }}>
        <div style={{
          fontSize: '1.5rem',
          color: '#e0e0e0',
          textAlign: 'center'
        }}>
          <img src="/WorkVan-logo-dark.png" alt="Work Van" style={{ width: '120px', marginBottom: '1.5rem' }} />
          <div>Loading your workspace...</div>
        </div>
      </div>
    );
  }

  // Show Welcome page after successful checkout
  // Only show if: setup was just completed AND we haven't dismissed the welcome page yet
  const shouldShowWelcome = (showWelcomePage ||
    (localStorage.getItem('setup_just_completed') === 'true' &&
     localStorage.getItem('welcome_page_dismissed') !== 'true'))
    && user && hasCompany && companyData;

  if (shouldShowWelcome) {
    return (
      <WelcomePage
        companyName={companyData?.name}
        onContinue={() => {
          // Mark welcome as dismissed
          localStorage.setItem('welcome_page_dismissed', 'true');
          setShowWelcomePage(false);
          // Force navigation — use both state update AND hard URL change as fallback
          try {
            navigateTo('home', null, { replace: true });
          } catch (e) {
            // Fallback if navigateTo fails
          }
          // Safety net: if still on welcome page after 500ms, force reload
          setTimeout(() => {
            if (localStorage.getItem('welcome_page_dismissed') === 'true') {
              window.location.hash = '#home';
              window.location.reload();
            }
          }, 500);
        }}
      />
    );
  }

  // Show CompanySignup if user has no company at all
  if (hasCompany === false) {
    return (
      <CompanySignup
        user={user}
        onComplete={(newCompanyData) => {
          // If company data is passed directly from signup, use it immediately
          if (newCompanyData) {
            setCompanyData({ ...newCompanyData, user_role: 'owner' });
            localStorage.setItem(`company_${user.id}`, JSON.stringify({ ...newCompanyData, user_role: 'owner' }));
            setHasCompany(true);
          } else {
            // Fallback: re-fetch from database
            setHasCompany(true);
            checkUserCompany(user.id);
          }
        }}
      />
    );
  }

  // Gate: If company exists but no Stripe subscription, require checkout
  // Allow through if: setup was just completed (webhook may be slow), user is NOT the owner,
  // or the company is a demo/sandbox account (the playground doesn't need checkout)
  const needsCheckout = hasCompany && companyData && !companyData.stripe_customer_id
    && !setupJustCompleted && localStorage.getItem('setup_just_completed') !== 'true'
    && companyData.user_role === 'owner'
    && companyData.tier !== 'free'
    && !companyData.is_demo;

  if (needsCheckout) {
    return (
      <div style={{
        height: '100vh',
        height: '100dvh',
        background: '#0a0a0a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '100%',
          padding: '2.5rem',
          border: '1px solid #3a3a3a',
          textAlign: 'center'
        }}>
          <img src="/WorkVan-logo-dark.png" alt="Work Van" style={{ height: '80px', marginBottom: '1.5rem' }} />
          <h2 style={{ color: '#e0e0e0', fontSize: '1.5rem', marginBottom: '0.5rem' }}>
            Complete Your Setup
          </h2>
          <p style={{ color: '#888', marginBottom: '1.5rem', lineHeight: '1.5' }}>
            Your account <strong style={{ color: '#e0e0e0' }}>{companyData.name}</strong> is ready!
            Set up your payment method to start your 14-day free trial. You won't be charged today.
          </p>
          <button
            id="checkout-btn"
            onClick={async (e) => {
              const btn = e.currentTarget;
              if (btn.disabled) return;
              btn.disabled = true;
              btn.textContent = 'Redirecting to Stripe...';
              try {
                const response = await fetch('/api/checkout', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    companyId: companyData.id,
                    tier: companyData.tier || 'pro',
                    interval: companyData.billing_interval || 'weekly',
                    userEmail: user.email
                  }),
                });
                const data = await response.json();
                if (data.url) {
                  window.location.href = data.url;
                } else {
                  const errMsg = data.error || 'No checkout URL returned';
                  console.error('[Checkout] API error:', errMsg);
                  alert('Payment setup error: ' + errMsg);
                  btn.disabled = false;
                  btn.textContent = 'Continue to Payment Setup';
                }
              } catch (err) {
                console.error('[Checkout] Error:', err);
                alert('Could not connect to payment service. Please try again.');
                btn.disabled = false;
                btn.textContent = 'Continue to Payment Setup';
              }
            }}
            style={{
              width: '100%',
              padding: '1rem',
              background: '#F05A28',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginBottom: '0.75rem'
            }}
          >
            Continue to Payment Setup
          </button>
          <button
            onClick={() => {
              localStorage.setItem('setup_just_completed', 'true');
              localStorage.setItem('setup_completed_at', Date.now().toString());
              window.location.reload();
            }}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'transparent',
              border: '1px solid #3a3a3a',
              color: '#aaa',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginBottom: '0.75rem'
            }}
          >
            Skip for now (start free trial)
          </button>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.href = '/';
            }}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#666',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              fontSize: '0.85rem'
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <FeatureProvider companyId={companyData?.id}>
    <div style={{
      display: 'flex',
      height: '100vh',
      height: '100dvh', // iOS Safari dynamic viewport
      background: colors.bg,
      color: colors.text,
      overflowX: 'hidden'
    }}>
      {/* PWA Install Prompt */}
      <InstallPrompt />

      {/* Sidebar */}
      {!isMobile && (
        <aside style={{
          width: sidebarCollapsed ? '64px' : '240px',
          background: colors.sidebarBg,
          borderRight: `1px solid ${colors.border}`,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.2s ease',
          flexShrink: 0,
          height: '100%',
          overflow: 'hidden'
        }}>
          {/* Header with Logo and Toggle */}
          <div style={{
            padding: sidebarCollapsed ? '0.75rem 0.5rem' : '1rem',
            borderBottom: `1px solid ${colors.border}`,
            display: 'flex',
            flexDirection: sidebarCollapsed ? 'column' : 'row',
            alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'space-between',
            gap: sidebarCollapsed ? '0.5rem' : '0.75rem',
            minHeight: '64px'
          }}>
            <div
              onClick={() => navigateTo('home')}
              style={{ cursor: 'pointer' }}
              title="Go to Today"
            >
              {companyData?.logo_url ? (
                <img
                  src={companyData.logo_url}
                  alt="Company Logo"
                  style={{
                    width: sidebarCollapsed ? '32px' : '40px',
                    height: sidebarCollapsed ? '32px' : '40px',
                    borderRadius: '8px',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <img
                  src={workvanLogoSrc}
                  alt="WorkVan"
                  style={{
                    height: sidebarCollapsed ? '36px' : '48px',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
            {!sidebarCollapsed && (
              <span style={{
                fontSize: '1.1rem',
                fontWeight: 'bold',
                color: colors.accent,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1
              }}>
                {companyData?.name || 'Work Van'}
              </span>
            )}

            {/* Collapse Toggle Button */}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                background: 'transparent',
                border: `1px solid ${colors.border}`,
                color: colors.textMuted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = colors.sidebarHover;
                e.currentTarget.style.color = colors.text;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.textMuted;
              }}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
            </button>
          </div>

          {/* Menu Items */}
          <nav data-tour-id="sidebar-nav" style={{ flex: 1, padding: '0.5rem 0', overflowY: 'auto', overflowX: 'hidden' }}>
            {menuItems.map(item => (
              <button
                key={item.id}
                data-tour-id={"menu-" + item.id}
                onClick={() => navigateTo(item.id)}
                title={sidebarCollapsed ? item.label : ''}
                style={{
                  width: 'calc(100% - 1rem)',
                  margin: '0.25rem 0.5rem',
                  padding: sidebarCollapsed ? '0.625rem' : '0.625rem 0.75rem',
                  background: currentModule === item.id ? colors.sidebarActive : 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: currentModule === item.id ? colors.accent : colors.sidebarText,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  gap: sidebarCollapsed ? 0 : '0.75rem',
                  transition: 'all 0.15s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = colors.sidebarHover}
                onMouseOut={(e) => e.currentTarget.style.background = currentModule === item.id ? colors.sidebarHover : 'transparent'}
              >
                <span>{item.icon}</span>
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            ))}
          </nav>

          {/* Send Feedback Button */}
          <div style={{ padding: '0 0.5rem 0.25rem' }}>
            <button
              onClick={() => setFeedbackOpen(true)}
              title={sidebarCollapsed ? 'Send Feedback' : ''}
              style={{
                width: '100%',
                padding: sidebarCollapsed ? '0.625rem' : '0.625rem 0.75rem',
                background: 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: colors.sidebarText,
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                gap: sidebarCollapsed ? 0 : '0.75rem',
                transition: 'all 0.15s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = colors.sidebarHover}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <MessageSquare size={20} />
              {!sidebarCollapsed && <span>Send Feedback</span>}
            </button>
          </div>

          {/* Support Chat Button */}
          <div style={{ padding: '0 0.5rem 0.25rem' }}>
            <button
              onClick={() => setSupportChatOpen(true)}
              title={sidebarCollapsed ? 'Support' : ''}
              style={{
                width: '100%',
                padding: sidebarCollapsed ? '0.625rem' : '0.625rem 0.75rem',
                background: 'transparent',
                border: 'none',
                borderRadius: '8px',
                color: colors.sidebarText,
                textAlign: 'left',
                cursor: 'pointer',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                gap: sidebarCollapsed ? 0 : '0.75rem',
                transition: 'all 0.15s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = colors.sidebarHover}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <MessageCircle size={20} />
              {!sidebarCollapsed && 'Support'}
            </button>
          </div>

          {/* Footer: User & Logout */}
          <div style={{
            padding: sidebarCollapsed ? '0.5rem' : '0.75rem',
            borderTop: `1px solid ${colors.border}`
          }}>
            {!sidebarCollapsed && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
                fontSize: '0.85rem',
                padding: '0 0.5rem'
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  background: colors.sidebarHover,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <UserIcon size={16} />
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <div style={{
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {userProfile?.full_name || user.email?.split('@')[0]}
                  </div>
                </div>
              </div>
            )}
            <button
              onClick={handleLogout}
              title="Logout"
              style={{
                width: '100%',
                padding: sidebarCollapsed ? '0.5rem' : '0.5rem 0.75rem',
                background: 'transparent',
                border: 'none',
                borderRadius: '6px',
                color: colors.textMuted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                gap: '0.5rem',
                fontSize: '0.85rem',
                transition: 'all 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = colors.sidebarHover;
                e.currentTarget.style.color = colors.text;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.textMuted;
              }}
            >
              <LogOut size={16} />
              {!sidebarCollapsed && 'Logout'}
            </button>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <main ref={mainRef} style={{
        flex: 1,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        position: 'relative'
      }}>
        {/* Trial-status banner — shows for non-demo accounts on a free trial */}
        <TrialBanner
          companyData={companyData}
          onUpgradeClick={() => navigateTo('settings', { section: 'subscription' })}
        />

        {/* Pull-to-Refresh Indicator */}
        {(pullDistance > 0 || isRefreshing) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: pullDistance > 0 ? `${pullDistance}px` : '40px',
            overflow: 'hidden',
            transition: isPulling ? 'none' : 'height 0.3s ease',
            flexShrink: 0
          }}>
            <RefreshCw
              size={20}
              style={{
                color: pullDistance >= 40 ? colors.accent : colors.textMuted,
                transform: `rotate(${pullDistance * 3}deg)`,
                transition: isPulling ? 'none' : 'transform 0.3s ease',
                animation: isRefreshing ? 'ptr-spin 0.8s linear infinite' : 'none'
              }}
            />
            <span style={{
              marginLeft: '0.5rem',
              fontSize: '0.8rem',
              color: pullDistance >= 40 ? colors.accent : colors.textMuted
            }}>
              {isRefreshing ? 'Refreshing...' : pullDistance >= 40 ? 'Release to refresh' : 'Pull to refresh'}
            </span>
          </div>
        )}

        {/* New Version Available Banner */}
        {newVersionAvailable && (
          <div style={{
            background: colors.accent,
            color: '#fff',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.75rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            flexShrink: 0,
            zIndex: 200
          }}>
            <span>A new version is available</span>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid rgba(255,255,255,0.4)',
                color: '#fff',
                padding: '0.25rem 0.75rem',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 600
              }}
            >
              Refresh
            </button>
            <button
              onClick={() => setNewVersionAvailable(false)}
              style={{
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                padding: '0.25rem',
                fontSize: '1rem',
                lineHeight: 1
              }}
            >
              x
            </button>
          </div>
        )}

        {/* Desktop Notification Bell - fixed top-right */}
        {!isMobile && (
          <div style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button
              onClick={() => window.location.reload()}
              title="Refresh page"
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: colors.bgCard,
                border: `1px solid ${colors.border}`,
                color: colors.textMuted,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                transition: 'all 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = colors.accent;
                e.currentTarget.style.borderColor = colors.accent;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = colors.textMuted;
                e.currentTarget.style.borderColor = colors.border;
              }}
            >
              <RefreshCw size={15} />
            </button>
            <NotificationBell
              companyId={companyData?.id}
              userId={user?.id}
              onNavigate={(module, data) => navigateTo(module, data)}
            />
          </div>
        )}
        {/* Mobile Header */}
        {isMobile && (
          <header style={{
            background: colors.bgCard,
            borderBottom: `1px solid ${colors.border}`,
            padding: '1rem',
            paddingTop: 'max(1rem, env(safe-area-inset-top))',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <div
              onClick={() => navigateTo('home')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: 'pointer'
              }}
            >
              {companyData?.logo_url ? (
                <img
                  src={companyData.logo_url}
                  alt="Logo"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    objectFit: 'contain'
                  }}
                />
              ) : (
                <img
                  src={workvanLogoSrc}
                  alt="WorkVan"
                  style={{
                    height: '36px',
                    objectFit: 'contain'
                  }}
                />
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <button
                onClick={() => window.location.reload()}
                title="Refresh page"
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <RefreshCw size={18} />
              </button>
              <NotificationBell
                companyId={companyData?.id}
                userId={user?.id}
                onNavigate={(module, data) => {
                  navigateTo(module, data);
                }}
              />
              <button
                data-tour-id="mobile-menu-button"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              style={{
                background: 'none',
                border: 'none',
                color: colors.text,
                cursor: 'pointer',
                padding: '0.5rem'
              }}
            >
              {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            </div>
          </header>
        )}

        {/* Mobile Sidebar Overlay */}
        {isMobile && sidebarOpen && (
          <div
            data-tour-id="mobile-sidebar-overlay"
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              zIndex: 999
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '80%',
                maxWidth: '300px',
                height: '100%',
                background: colors.bgCard,
                padding: '1.5rem',
                paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
                overflowY: 'auto'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '2rem'
              }}>
                {companyData?.logo_url ? (
                  <img
                    src={companyData.logo_url}
                    alt="Company Logo"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <img
                    src={workvanLogoSrc}
                    alt="WorkVan"
                    style={{
                      height: '36px',
                      objectFit: 'contain'
                    }}
                  />
                )}
                <span style={{
                  fontSize: '1.25rem',
                  fontWeight: 'bold',
                  color: colors.accent
                }}>
                  {companyData?.name || 'Work Van'}
                </span>
              </div>

              {companyData && (
                <div style={{
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: colors.bgInput,
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                    {companyData.name}
                  </div>
                  <div style={{ color: colors.textMuted, textTransform: 'capitalize' }}>
                    {companyData.tier} Plan • {companyData.subscription_status}
                  </div>
                </div>
              )}

              <nav style={{ marginBottom: '2rem' }}>
                {menuItems.map(item => (
                  <button
                    key={item.id}
                    data-tour-id={"menu-mobile-" + item.id}
                    onClick={() => {
                      navigateTo(item.id);
                      setSidebarOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: currentModule === item.id ? colors.bgInput : 'transparent',
                      border: 'none',
                      color: currentModule === item.id ? colors.accent : colors.text,
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      borderRadius: '6px',
                      marginBottom: '0.25rem'
                    }}
                  >
                    <span>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
                <button
                  onClick={() => { setFeedbackOpen(true); setSidebarOpen(false); }}
                  style={{
                    width: '100%',
                    padding: '0.875rem 1rem',
                    background: 'transparent',
                    border: 'none',
                    color: colors.sidebarText,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    borderRadius: '6px',
                    marginBottom: '0.25rem'
                  }}
                >
                  <MessageSquare size={20} />
                  <span>Send Feedback</span>
                </button>
              </nav>

              <div style={{
                padding: '1rem',
                background: colors.bgInput,
                borderRadius: '8px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '0.75rem',
                  fontSize: '0.9rem'
                }}>
                  <UserIcon size={20} />
                  <div>
                    <div style={{ fontWeight: 'bold' }}>
                      {userProfile?.full_name || user.email}
                    </div>
                    {userProfile?.role && (
                      <div style={{ fontSize: '0.75rem', color: colors.textMuted, textTransform: 'capitalize' }}>
                        {userProfile.role}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    background: colors.bgHover,
                    border: 'none',
                    borderRadius: '6px',
                    color: colors.text,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <LogOut size={16} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Module Content */}
        <div key={moduleKey} style={{ flex: 1, padding: isMobile ? '1rem' : '2rem', paddingRight: isMobile ? '1rem' : '5rem' }}>
          {renderModule()}
        </div>
      </main>

      {/* Demo Tier Switcher - visible only for demo accounts */}
      {companyData?.is_demo && (
        <DemoTierSwitcher
          companyId={companyData.id}
          currentTier={companyData.tier}
          currentBusinessType={companyData.business_type}
          onTierChanged={(newTier) => {
            const tierMaxUsers = { pro: 1, team: 3, growth: 5, business: 10 };
            setCompanyData(prev => ({ ...prev, tier: newTier, max_users: tierMaxUsers[newTier] || 5, purchased_seats: 0 }));
          }}
          onIndustryChanged={(newIndustry) => {
            setCompanyData(prev => ({ ...prev, business_type: newIndustry }));
            // Hard refresh — every list/table on the page is now stale
            setTimeout(() => window.location.reload(), 400);
          }}
        />
      )}

      {/* Smart Assistant - 15-min job alert */}
      <SmartAssistant
        companyId={companyData?.id}
        user={user}
        jobs={jobsForAssistant}
        companyName={companyData?.name}
      />

      {/* AI Support Chat Widget with Live Agent Support */}
      <SupportChat
        companyId={companyData?.id}
        userEmail={user?.email}
        userId={user?.id}
        userName={userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0]}
        isOpen={supportChatOpen}
        onClose={() => setSupportChatOpen(false)}
      />

      {/* Feedback Modal — goes to founder via SMS */}
      <FeedbackModal
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        companyId={companyData?.id}
        user={user}
        companyName={companyData?.name}
      />

      {/* Guided Tour Overlay */}
      <GuidedTour />

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: '1rem'
          }}
          onClick={cancelLogout}
        >
          <div 
            style={{
              background: colors.bgCard,
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '400px',
              width: '100%',
              border: `1px solid ${colors.border}`,
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              animation: 'modalSlideIn 0.2s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: `${colors.error}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem auto'
            }}>
              <LogOut size={28} style={{ color: colors.error }} />
            </div>
            <h3 style={{ color: colors.text, margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
              Sign Out?
            </h3>
            <p style={{ color: colors.textMuted, fontSize: '0.95rem', marginBottom: '2rem', lineHeight: '1.5' }}>
              Any unsaved changes will be lost. You can sign back in anytime.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={cancelLogout}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.bgInput,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '10px',
                  color: colors.text,
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = colors.bgHover;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = colors.bgInput;
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                style={{
                  flex: 1,
                  padding: '0.875rem',
                  background: colors.error,
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: `0 4px 14px ${colors.error}40`
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#dc2626';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = colors.error;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Sign Out
              </button>
            </div>
          </div>
          <style>{`
            @keyframes modalSlideIn {
              from {
                opacity: 0;
                transform: scale(0.95) translateY(-10px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
          `}</style>
        </div>
      )}

      {/* Pull-to-refresh spinner animation */}
      <style>{`
        @keyframes ptr-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
    </FeatureProvider>
  );
}

// Wrap App with ThemeProvider and ModalProvider
function AppWithProviders() {
  return (
    <ThemeProvider>
      <ModalProvider>
        <GuidedTourProvider>
          <App />
        </GuidedTourProvider>
      </ModalProvider>
    </ThemeProvider>
  );
}

export default AppWithProviders;
